// ============================================================
// صدای اعلان دستیار
//
// دو نت سینوسی با Web Audio ساخته می‌شود — بدون فایل صوتی و بدون
// هیچ درخواست شبکه‌ای.
//
// دو درس در این فایل هست:
//   ۱) مرورگرها سقف تعداد AudioContext دارند (کروم حدود شش)، پس یک
//      نمونه‌ی مشترک نگه داشته می‌شود.
//   ۲) نمونه‌ی *فعال* دستگاه صوتی را اشغال نگه می‌دارد و با میکروفون
//      تداخل می‌کند، پس بعد از پخش suspend() می‌شود.
// ============================================================

const KEY = "havir-assistant-chime";

type Tone = "done" | "ask";

let context: AudioContext | null = null;
let enabled = true;

export const loadChimePreference = (): boolean => {
    try {
        // پیش‌فرض روشن است؛ فقط خاموشی صریح ذخیره می‌شود
        enabled = localStorage.getItem(KEY) !== "0";
    } catch {
        enabled = true;
    }
    return enabled;
};

export const setChimeEnabled = (value: boolean): void => {
    enabled = !!value;
    try {
        localStorage.setItem(KEY, enabled ? "1" : "0");
    } catch {
        /* حالت خصوصی مرورگر — نادیده */
    }
};

export const isChimeEnabled = (): boolean => enabled;

const getContext = (): AudioContext | null => {
    if (typeof window === "undefined") return null;
    const Ctor: any = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;

    if (!context) {
        try {
            context = new Ctor();
        } catch {
            return null;
        }
    }
    return context;
};

/**
 * «done» صدای دو نت بالارونده (کاری انجام شد).
 * «ask» یک نت کوتاه‌تر و پایین‌تر (سوالی پرسیده شد) — تا کاربر از
 * روی صدا بفهمد منتظر پاسخ اوییم، بدون اینکه به صفحه نگاه کند.
 */
export const chime = (tone: Tone = "done"): void => {
    if (!enabled) return;

    const ctx = getContext();
    if (!ctx) return;

    try {
        // مرورگر بدون تعامل کاربر صدا پخش نمی‌کند؛ اینجا همیشه بعد از
        // کلیک یا تایپ کاربر صدا زده می‌شود، پس resume امن است.
        if (ctx.state === "suspended" && ctx.resume) ctx.resume();

        const notes = tone === "done" ? [880, 1174.7] : [659.3, 523.3];
        const now = ctx.currentTime;

        notes.forEach((frequency, index) => {
            const start = now + index * 0.12;
            const oscillator = ctx.createOscillator();
            const gain = ctx.createGain();

            oscillator.type = "sine";
            oscillator.frequency.value = frequency;

            // پاکت صدا: بالا رفتن سریع و افت نرم، وگرنه «تق» می‌دهد
            gain.gain.setValueAtTime(0.0001, start);
            gain.gain.exponentialRampToValueAtTime(0.09, start + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.18);

            oscillator.connect(gain);
            gain.connect(ctx.destination);
            oscillator.start(start);
            oscillator.stop(start + 0.2);
        });

        // دستگاه صوتی را آزاد می‌کنیم تا با میکروفون تداخل نکند
        window.setTimeout(() => {
            try {
                if (context && context.state === "running") context.suspend();
            } catch {
                /* نادیده */
            }
        }, 600);
    } catch {
        // نبود صدا نباید جلوی کار دستیار را بگیرد
    }
};
